import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash.startcase';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import ModalButtons from '../Modal/ModalButtons';
import CheckboxGTT from '../InputFields/CheckboxGTT';
import PrintModal from '../PrintModal/PrintModal';
import Paragraph from '../Paragraph/Paragraph';
import ReviewInfoRow from './ReviewInfoRow';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import {
  getCurrentAccountUid,
  selectAccountAgreementHtml,
} from '../../store/selectors/accountSelectors';
import {
  fetchAccountAgreement,
  submitAccountAgreements,
} from '../../store/actions/account/accountActions';
import { FETCH_ACCOUNT_AGREEMENT_HTML_LOADING } from '../../store/actions/account/accountActionConstants';
import { executeTriggerOrderQuote } from '../../store/actions/triggerOrders/triggerOrdersActions';
import { EXECUTE_TRIGGER_ORDER_QUOTE_LOADING } from '../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import { AGREEMENT_TYPES } from '../../constants/agreementConstants';
import AgreementDisplay from '../Agreements/AgreementDisplay';
import config from '../../config';
import { mediaBelow } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import useGtmHook from '../../util/hooks/useGtmHook';
import { trackTriggerOrderReviewed } from './Common/gtmEventHelper';
import {
  ReviewProductRow,
  ReviewThumb,
  ReviewProductInfo,
  ReviewOrderLabel,
  ReviewProductName,
  StoringInVaultText,
  ReviewVaultRow,
  SegregatedTag,
  ReviewDivider,
  PriceCaption,
  VolumeDiscountBanner,
  VolumeDiscountIcon,
  ReservedFundsBanner,
  ReservedFundsText,
  ReviewFooterWrapper,
  ReviewTermsRow,
  ReviewTermsText,
  ReviewTermsLink,
} from './Common';
import { variables } from '../../assets/styles/variables';

// Third wizard step: shows a read-only summary of the target price order set
// up in TargetPriceOrderEntry (product, vault, subtotal, savings, total) and
// requires the terms checkbox before continuing (no-op until Confirmation is
// built). Modeled on TargetPriceOrderEntry for the wizard scaffolding,
// Buy/OrderReviewPriceList for the price-summary rows, and
// Modals/MetalStream/MetalStreamReview for the terms-checkbox + disabled-
// submit pattern.
const TargetPriceOrderReview = ({
  wizardData,
  setWizardData,
  setWizardTitle,
  setTitleFontSizeMobile,
  setTitleLineHeightMobile,
  setGoBackHandler,
  handleBack,
  handleNext,
  isLastStep,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ecommerceGtmEvent } = useGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const { RootUrl, SegregatedCaption } = useSelector(selectSettings);

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isAgreementDownloading, setIsAgreementDownloading] = useState(false);
  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState(false);
  // Mirrors AlertEntry: the agreement HTML is fetched inside AgreementDisplay
  // (the terms modal's children), so treat the modal as "content loading"
  // while that fetch is in flight OR the HTML hasn't arrived yet.
  const agreementHtml = useSelector(selectAccountAgreementHtml);
  const isAgreementHtmlLoading = useSelector(
    selectIsLoadingByActionTypes([FETCH_ACCOUNT_AGREEMENT_HTML_LOADING]),
  );
  const isAgreementContentLoading = isAgreementHtmlLoading || !agreementHtml;
  const isExecutingQuote = useSelector(
    selectIsLoadingByActionTypes([EXECUTE_TRIGGER_ORDER_QUOTE_LOADING]),
  );

  const product = wizardData?.selectedProduct;
  const vault = wizardData?.selectedVault;
  const quantity = Number(wizardData?.orderValues?.QuantityAmount) || 0;
  const targetPrice = Number(wizardData?.orderValues?.CashAmount) || 0;
  // The quote fetched by TargetPriceOrderEntry's GET trigger-orders/quotes
  // call (on "Review Target Order"), stashed into wizardData. BE is
  // authoritative for these once the quote exists.
  const quote = wizardData?.quote;
  const volumeDiscount = Number(quote?.VolumeDiscount) || 0;

  // The live price captured on the Entry screen when the quote was
  // requested — reused here instead of re-fetching so "Current Price"
  // matches what the user saw there rather than drifting on a fresh fetch.
  const productPrice = Number(wizardData?.productPrice) || 0;

  const vaultName = vault
    ? startCase(parseEnumType(productLocationTypes, vault.LocationType))
    : '';
  const isSegregatedStorage = wizardData?.selectedPlan === 'premium';

  const subtotal = Number(quote?.Subtotal) || 0;
  const savings = Number(quote?.TargetPriceSavings) || 0;
  const totalAtExecution = Number(quote?.TotalAtExecution) || 0;

  const imageSource = product
    ? config.algoliaSearch.imageCode
        .replace('{product_code}', product.ProductCode || product.Code)
        .replace('{base_url}', RootUrl)
    : '';

  // Set the header title and route the back chevron to the previous step.
  useEffect(() => {
    setWizardTitle(t('gtt.triggerOrders.review.title'));
    setTitleFontSizeMobile(18);
    setTitleLineHeightMobile('130%');
    setGoBackHandler(() => handleBack);
    return () => {
      setTitleFontSizeMobile(undefined);
      setTitleLineHeightMobile(undefined);
      setGoBackHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Download / view the trigger orders terms-of-use agreement PDF. Mirrors
  // AlertEntry's handleDownloadAgreement: the saga GETs
  // /accounts/{accountUid}/agreements/pdf as a blob and we open it in a new
  // tab. AGREEMENT_TYPES[10] matches the type rendered by AgreementDisplay in
  // the terms modal below.
  const handleDownloadAgreement = () => {
    dispatch(
      fetchAccountAgreement({
        accountUid,
        agreementType: AGREEMENT_TYPES[10],
        setIsLoading: setIsAgreementDownloading,
        agreementFetchSuccess: (data) => {
          if (data) {
            const objectUrl = window.URL.createObjectURL(data);
            window.open(objectUrl, '_blank', 'noopener,noreferrer');
          }
        },
      }),
    );
  };

  // Accept the terms from within the modal — records consent in the client
  // (checks the box, closes the modal). The actual acceptance is posted at
  // submit time (see handleSubmit), same as AlertEntry's SMS consent.
  const handleAcceptAgreement = () => {
    setWizardData({ areTermsChecked: true });
    setIsTermsModalOpen(false);
  };

  const handleSubmit = () => {
    // Record the trigger orders terms acceptance server-side, then execute
    // the quote fetched on Review entry (POST trigger-orders/quotes). Gate
    // advancing to the next step on both calls succeeding.
    dispatch(
      submitAccountAgreements({
        accountUid,
        agreementType: AGREEMENT_TYPES[10],
        setIsLoading: setIsSubmittingAgreement,
        onSuccess: () => {
          dispatch(
            executeTriggerOrderQuote({
              accountUid,
              QuoteUid: quote?.QuoteUid,
              onSuccess: () => {
                trackTriggerOrderReviewed({
                  ecommerceGtmEvent,
                  productCode: product?.ProductCode || product?.Code,
                  targetPrice,
                  quantity,
                  vault: vaultName,
                });
                // Confirmation screen isn't built yet, so don't advance off
                // the last defined step — that would blank the modal
                // (mirrors ProductSelection / OrderEntry).
                if (!isLastStep) {
                  handleNext();
                }
              },
            }),
          );
        },
      }),
    );
  };

  return (
    <>
      <SectionLoader isLoading={isSubmittingAgreement || isExecutingQuote}>
        <BasicModalBody>
          <ReviewProductRow>
            <ReviewThumb src={encodeURI(imageSource)} alt={product?.Caption} />
            <ReviewProductInfo>
              <ReviewOrderLabel>
                {t('gtt.triggerOrders.review.targetOrder')}
              </ReviewOrderLabel>
              <ReviewProductName>
                {quantity} × {product?.ShortCaption}
              </ReviewProductName>
              <ReviewVaultRow>
                <StoringInVaultText
                  translationKey="gtt.triggerOrders.review.storingIn"
                  vaultName={vaultName}
                  fontSizeMobile={16}
                  fontWeightMobile={400}
                />
                {isSegregatedStorage && (
                  <SegregatedTag>
                    {SegregatedCaption || t('buyWizard.premium')}
                  </SegregatedTag>
                )}
              </ReviewVaultRow>
            </ReviewProductInfo>
          </ReviewProductRow>

          <ReviewDivider />

          <ReviewInfoRow
            label={t('gtt.triggerOrders.review.subtotal', { quantity })}
            value={formatMoneyNumeral(subtotal)}
            fontFamily={variables.family.goldSilver}
            labelFontSize={14}
            usePxForLabelFontSize
            labelFontWeight={400}
            labelLineHeight="140%"
            labelFontSizeMobile={16}
            labelFontWeightMobile={400}
            valueFontSize={14}
            usePxForValueFontSize
            valueFontWeight={400}
            valueLineHeight="140%"
            valueFontSizeMobile={18}
            valueFontWeightMobile={400}
          />
          <PriceCaption>
            {t('gtt.triggerOrders.review.currentPrice', {
              price: formatMoneyNumeral(productPrice),
            })}
          </PriceCaption>

          <ReviewInfoRow
            label={t('gtt.triggerOrders.review.targetPriceSavings')}
            value={`-${formatMoneyNumeral(savings)}`}
            fontFamily={variables.family.goldSilver}
            labelFontSize={14}
            usePxForLabelFontSize
            labelFontWeight={400}
            labelLineHeight="140%"
            labelFontSizeMobile={16}
            labelFontWeightMobile={400}
            valueColor={themeColors.colorBestFlag}
            valueFontSize={14}
            usePxForValueFontSize
            valueFontWeight={500}
            valueLineHeight="140%"
            valueFontSizeMobile={17}
            valueFontWeightMobile={400}
          />

          <ReviewDivider />

          <ReviewInfoRow
            label={t('gtt.triggerOrders.review.totalAtExecution')}
            value={formatMoneyNumeral(totalAtExecution)}
            fontFamily={variables.family.goldSilver}
            labelFontSize={14}
            usePxForLabelFontSize
            labelFontWeight={400}
            labelLineHeight="140%"
            labelFontSizeMobile={16}
            labelFontWeightMobile={400}
            valueFontSize={14}
            usePxForValueFontSize
            valueFontWeight={400}
            valueLineHeight="100%"
            valueFontSizeMobile={18}
            valueFontWeightMobile={600}
            isValueBold
          />
          <PriceCaption>
            {t('gtt.triggerOrders.review.targetPriceCaption', {
              price: formatMoneyNumeral(targetPrice),
              quantity,
            })}
          </PriceCaption>

          <ReviewDivider />

          {volumeDiscount > 0 && (
            <VolumeDiscountBanner>
              <VolumeDiscountIcon />
              <Paragraph
                fontSize={14}
                usePx
                fontWeight={600}
                fontFamily={variables.family.goldSilver}
                lineHeight="140%"
                color={themeColors.colorBestFlag}
                fontSizeMobile={16}
              >
                <b>{formatMoneyNumeral(volumeDiscount)}</b>{' '}
                {t('gtt.triggerOrders.review.volumeDiscountText')}
              </Paragraph>
            </VolumeDiscountBanner>
          )}

          <ReservedFundsBanner>
            <InfoIcon
              fill={themeColors.colorTextSecondary}
              style={{ width: 18, height: 18 }}
            />
            <ReservedFundsText
              fontSize={14}
              usePx
              fontWeight={400}
              fontFamily={variables.family.goldSilver}
              color={themeColors.colorTextSecondary}
            >
              <b>{formatMoneyNumeral(totalAtExecution)}</b>{' '}
              {t('gtt.triggerOrders.review.reservedFundsText')}
            </ReservedFundsText>
          </ReservedFundsBanner>

          <ReviewFooterWrapper>
            <ReviewDivider fullBleed />
            <ReviewTermsRow>
              <CheckboxGTT
                checked={!!wizardData?.areTermsChecked}
                onClick={() =>
                  setWizardData({
                    areTermsChecked: !wizardData?.areTermsChecked,
                  })
                }
                name="target-price-terms"
                ariaLabel={t('gtt.triggerOrders.review.agreeText')}
                raisedCheckmark
                lightBackground
              />
              <ReviewTermsText>
                {t('gtt.triggerOrders.review.agreeText')}{' '}
                <ReviewTermsLink
                  role="link"
                  tabIndex={0}
                  data-cy="link-target-price-terms"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setIsTermsModalOpen(true);
                  }}
                >
                  {t('gtt.triggerOrders.review.termsLink')}
                </ReviewTermsLink>
              </ReviewTermsText>
            </ReviewTermsRow>

            <ModalButtons
              showOnlyPrimary
              // Scoped to this instance via ModalButtonsContainer's existing
              // `css` passthrough — the shared ModalButtons/Button components
              // themselves aren't touched, so no other screen is affected.
              css={`
                > button {
                  font-size: 16px;

                  ${mediaBelow(variables.breakpoints.bpMd)} {
                    font-size: 18px;
                  }
                }
              `}
              primaryButtonProps={{
                label: t('gtt.triggerOrders.review.setBuyTargetPrice'),
                disabled:
                  !wizardData?.areTermsChecked ||
                  !quote?.QuoteUid ||
                  isSubmittingAgreement ||
                  isExecutingQuote,
                textTransform: 'capitalize',
                onClick: handleSubmit,
                height: '44px',
                'data-cy': 'gtt-set-buy-target-price',
              }}
            />
          </ReviewFooterWrapper>
        </BasicModalBody>
      </SectionLoader>

      <PrintModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onCloseButton={handleAcceptAgreement}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
        shouldScrollDown
        hideHeaderShadow
        closeIconRight
        isLoading={isAgreementContentLoading || isSubmittingAgreement}
        isDownloading={isAgreementDownloading}
        onDownload={handleDownloadAgreement}
        downloadLabel={t('gtt.labels.downloadAgreement')}
      >
        <AgreementDisplay
          type={AGREEMENT_TYPES[10]}
          signTheDoc={false}
          hideLoader
        />
      </PrintModal>
    </>
  );
};

TargetPriceOrderReview.propTypes = {
  wizardData: PropTypes.shape({
    selectedProduct: PropTypes.shape({
      ProductCode: PropTypes.string,
      Code: PropTypes.string,
      Caption: PropTypes.string,
      WeightCaption: PropTypes.string,
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    areTermsChecked: PropTypes.bool,
    selectedPlan: PropTypes.string,
    productPrice: PropTypes.number,
    quote: PropTypes.shape({
      QuoteUid: PropTypes.string,
      Subtotal: PropTypes.number,
      TargetPriceSavings: PropTypes.number,
      TotalAtExecution: PropTypes.number,
      VolumeDiscount: PropTypes.number,
    }),
  }),
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setTitleFontSizeMobile: PropTypes.func,
  setTitleLineHeightMobile: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  isLastStep: PropTypes.bool,
};

export default TargetPriceOrderReview;
